import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import { useContentProtection } from '../../hooks/useContentProtection';
import { 
  Plus, 
  Play, 
  Trophy, 
  TrendingUp, 
  Clock, 
  Target, 
  Presentation, 
  ChevronRight,
  Flame,
  Star,
  Calendar,
  Video,
  FileText,
  ArrowUpRight,
  Sparkles
} from 'lucide-react';

const EvolveDashboard = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  // Enable content protection (disable right-click, copy, screenshots)
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch sessions
      const sessionsRes = await api.get('/evolve/sessions');
      setSessions(sessionsRes.data || []);
      
      // Calculate stats from sessions
      const completedSessions = sessionsRes.data?.filter(s => s.status === 'analyzed') || [];
      const scores = completedSessions.map(s => s.overall_score).filter(s => s !== null);
      const avgScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
      
      setStats({
        totalSessions: sessionsRes.data?.length || 0,
        completedSessions: completedSessions.length,
        averageScore: Math.round(avgScore),
        highestScore: scores.length > 0 ? Math.max(...scores) : 0,
        streak: 3, // TODO: Calculate actual streak
      });

      // Fetch system health
      try {
        const healthRes = await api.get('/evolve/admin/system/health');
        setSystemHealth(healthRes.data);
      } catch (e) {
        // Non-admin users won't have access - this is expected behavior
        if (process.env.NODE_ENV === 'development') {
          console.debug('System health not accessible (admin only):', e.message);
        }
      }
      
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'analyzed': return 'bg-emerald-100 text-emerald-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-amber-100 text-amber-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-rose-600';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#F9F8F6]">
        <div className="p-6 lg:p-8 max-w-7xl mx-auto">
          
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  EVOLVE
                </h1>
                <p className="text-slate-600 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  AI-Powered Presentation & Communication Coaching
                </p>
              </div>
              <Button 
                onClick={() => navigate('/evolve/new')}
                className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white gap-2 shadow-lg shadow-[#E07A5F]/25"
                data-testid="create-session-btn"
              >
                <Plus className="h-5 w-5" />
                New Session
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {loading ? (
              [...Array(4)].map((_, i) => (
                <Card key={i} className="bg-white border-slate-200">
                  <CardContent className="p-6">
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-8 w-16" />
                  </CardContent>
                </Card>
              ))
            ) : (
              <>
                <Card className="bg-white border-slate-200 hover:border-slate-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-[#E07A5F]/10">
                        <Presentation className="h-5 w-5 text-[#E07A5F]" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Total Sessions</p>
                        <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats?.totalSessions || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-slate-200 hover:border-slate-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-[#81B29A]/10">
                        <Target className="h-5 w-5 text-[#81B29A]" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Avg Score</p>
                        <p className={`text-2xl font-bold ${getScoreColor(stats?.averageScore)}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {stats?.averageScore || 0}%
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-slate-200 hover:border-slate-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-[#F2CC8F]/10">
                        <Trophy className="h-5 w-5 text-[#F2CC8F]" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Best Score</p>
                        <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats?.highestScore || 0}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-slate-200 hover:border-slate-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-rose-100">
                        <Flame className="h-5 w-5 text-rose-500" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Day Streak</p>
                        <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats?.streak || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-6">
            
            {/* Recent Sessions */}
            <div className="lg:col-span-2">
              <Card className="bg-white border-slate-200 h-full">
                <CardHeader className="border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Sessions</CardTitle>
                      <CardDescription style={{ fontFamily: 'Manrope, sans-serif' }}>Your presentation coaching history</CardDescription>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => navigate('/evolve/history')}
                      className="text-[#E07A5F] hover:text-[#D06A4F] hover:bg-[#E07A5F]/10"
                      data-testid="view-all-sessions-btn"
                    >
                      View All
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="p-6 space-y-4">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="flex items-center gap-4">
                          <Skeleton className="h-16 w-24 rounded-lg" />
                          <div className="flex-1">
                            <Skeleton className="h-4 w-48 mb-2" />
                            <Skeleton className="h-3 w-32" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : sessions.length === 0 ? (
                    <div className="p-12 text-center">
                      <div className="mx-auto w-16 h-16 rounded-full bg-[#E07A5F]/10 flex items-center justify-center mb-4">
                        <Presentation className="h-8 w-8 text-[#E07A5F]" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        No sessions yet
                      </h3>
                      <p className="text-slate-500 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Start your first presentation coaching session
                      </p>
                      <Button 
                        onClick={() => navigate('/evolve/new')}
                        className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
                        data-testid="first-session-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create Session
                      </Button>
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <div className="divide-y divide-slate-100">
                        {sessions.slice(0, 5).map((session) => (
                          <div 
                            key={session.id}
                            className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                            onClick={() => navigate(`/evolve/session/${session.id}`)}
                            data-testid={`session-item-${session.id}`}
                          >
                            <div className="flex items-start gap-4">
                              <div className="w-20 h-14 rounded-lg bg-gradient-to-br from-[#E07A5F]/20 to-[#81B29A]/20 flex items-center justify-center flex-shrink-0">
                                {session.slide_count > 0 ? (
                                  <FileText className="h-6 w-6 text-[#E07A5F]" />
                                ) : (
                                  <Presentation className="h-6 w-6 text-slate-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-semibold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                    {session.topic_name}
                                  </h4>
                                  <Badge className={getStatusColor(session.status)} variant="secondary">
                                    {session.status === 'analyzed' ? 'Completed' : session.status.replace('_', ' ')}
                                  </Badge>
                                </div>
                                <p className="text-sm text-slate-500 truncate mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                  {session.concept_overview}
                                </p>
                                <div className="flex items-center gap-4 text-xs text-slate-400">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {formatDate(session.created_at)}
                                  </span>
                                  {session.slide_count > 0 && (
                                    <span className="flex items-center gap-1">
                                      <FileText className="h-3 w-3" />
                                      {session.slide_count} slides
                                    </span>
                                  )}
                                  {session.overall_score && (
                                    <span className={`flex items-center gap-1 font-medium ${getScoreColor(session.overall_score)}`}>
                                      <Star className="h-3 w-3" />
                                      {session.overall_score}%
                                    </span>
                                  )}
                                </div>
                              </div>
                              <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions & Tips */}
            <div className="space-y-6">
              {/* Quick Start */}
              <Card className="bg-gradient-to-br from-[#E07A5F] to-[#D06A4F] border-0 text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
                <CardContent className="p-6 relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-white/20">
                      <Sparkles className="h-5 w-5" />
                    </div>
                    <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Quick Start</h3>
                  </div>
                  <p className="text-white/80 text-sm mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Upload your presentation and get AI-powered feedback on your delivery
                  </p>
                  <Button 
                    variant="secondary" 
                    className="w-full bg-white text-[#E07A5F] hover:bg-white/90"
                    onClick={() => navigate('/evolve/new')}
                    data-testid="quick-start-btn"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Start Now
                  </Button>
                </CardContent>
              </Card>

              {/* Tips */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <TrendingUp className="h-5 w-5 text-[#81B29A]" />
                    Pro Tips
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 rounded-full bg-[#81B29A]/10 mt-0.5">
                      <Video className="h-3.5 w-3.5 text-[#81B29A]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Use good lighting</p>
                      <p className="text-xs text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Face a window or light source for better presence analysis</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 rounded-full bg-[#F2CC8F]/10 mt-0.5">
                      <Clock className="h-3.5 w-3.5 text-[#F2CC8F]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Practice timing</p>
                      <p className="text-xs text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Aim for 2-3 minutes per slide on average</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 rounded-full bg-[#E07A5F]/10 mt-0.5">
                      <Target className="h-3.5 w-3.5 text-[#E07A5F]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Answer confidently</p>
                      <p className="text-xs text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Take a breath before answering Q&A questions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* System Status (Admin only) */}
              {systemHealth && (
                <Card className="bg-white border-slate-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Admin
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`h-2 w-2 rounded-full ${systemHealth.status === 'healthy' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                        <span className="text-sm text-slate-600 capitalize" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          System {systemHealth.status}
                        </span>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full border-slate-200 text-slate-600"
                      onClick={() => navigate('/evolve/analytics')}
                      data-testid="admin-analytics-btn"
                    >
                      <TrendingUp className="h-4 w-4 mr-2" />
                      View Analytics
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EvolveDashboard;
